import {
  HTML_LANG,
  LOCALE_LABEL,
  SUPPORTED_LOCALES,
  pathInLocale,
  setUiLocale,
  useTranslation,
  type UiLocale,
} from '@lb/i18n'
import { useSetLocale } from '@lb/domain'
import { cn } from '@lb/ui'

import { http } from '../../lib/client'
import { useSessionContext } from '../providers/session'

/**
 * The interface language.
 *
 * ── Why this is two buttons and not a dropdown ────────────────────────────
 * Two languages. A `<select>` for two options costs a tap to open, a tap to
 * choose, and a popup that covers the thing it is changing — where a segmented
 * pair changes it in one tap and shows both states at once.
 *
 * **Each label is written in its own language** — "Français", never "French".
 * Somebody who cannot read the current interface still has to be able to find
 * their way out of it, which is the whole job of this control.
 *
 * ── It is no longer purely client-side, and that changed ──────────────────
 * This shipped as a `localStorage` preference that needed no account and no
 * route, which was true at the time. It stopped being true when transactional
 * mail began resolving from `users.locale`: the language somebody reads the
 * product in and the language their password-reset email arrives in are now the
 * same decision, and only one half of it was reachable.
 *
 * So a signed-in member's choice is also written to their account. **On an
 * explicit press only** — never from a default. `resolveInitialLocale` guesses
 * from `navigator.language`, and writing that guess would mean signing in on a
 * hotel computer silently rewrote which language your security mail arrives in.
 *
 * ── The write outlives the navigation rather than delaying it ─────────────
 * The first version fired the request and navigated in the same tick, and a
 * full page navigation aborts in-flight fetches — so the POST was cancelled
 * every time and `users.locale` never moved. The toggle looked like it worked.
 * Caught by reading the session back after pressing it, not by any test.
 *
 * Awaiting the write before navigating fixed that and introduced a worse
 * problem: a round trip between the press and the URL changing, which is
 * exactly the window where the address bar disagrees with the page. So the
 * request is `keepalive` instead — it finishes after this page is gone, and the
 * navigation happens immediately as it always did.
 *
 * The failure is deliberately not surfaced. They asked to change the language,
 * not to be told about a mail preference, and the interface has already changed.
 */
export function LanguageToggle() {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const setLocale = useSetLocale(http)
  const active: UiLocale = i18n.language === 'en' ? 'en' : 'fr'

  return (
    <div
      role="group"
      aria-label={t('language.label')}
      className="flex items-center gap-1 px-3 py-1"
    >
      <span className="text-[13px] text-muted">{t('language.toggle')}</span>
      <span aria-hidden className="text-muted">
        ·
      </span>
      {SUPPORTED_LOCALES.map((locale) => (
        <button
          key={locale}
          /*
           * A full navigation, not just a state change.
           *
           * Each language now has its own URL, so switching has to move the
           * member to it — otherwise the address bar disagrees with what is on
           * screen, and copying the link sends somebody the other language.
           * `assign` rather than a client navigation because the router's
           * basepath is fixed at construction.
           */
          /* `void` on an async handler: React does not consume the promise,
             and the lint rule is right that returning one here means nothing. */
          onClick={() => {
            void setUiLocale(locale)

            /* Signed in only: there is no account to write to on the funnel,
               and the signup call carries the locale for that case. `keepalive`
               is what lets this survive the navigation below. */
            if (viewer) setLocale.mutate(locale)

            const next = pathInLocale(window.location.pathname, locale)
            if (next !== window.location.pathname) {
              window.location.assign(next + window.location.search)
            }
          }}
          aria-pressed={locale === active}
          /* `lang` on the button so a screen reader pronounces each label in
             the language it names, rather than in the page's language. */
          lang={HTML_LANG[locale]}
          className={cn(
            'min-h-[36px] rounded-[var(--r-sm)] px-2 text-[13px]',
            locale === active ? 'font-semibold text-ink' : 'text-muted hover:text-ink-soft',
          )}
        >
          {LOCALE_LABEL[locale]}
        </button>
      ))}
    </div>
  )
}

/**
 * The same control, sized for the topbar's icon row.
 *
 * `FR · EN` as two short buttons — no leading label, 36px hit targets — so it
 * sits in the action cluster without reading as a menu. Behaviour is the
 * shared one above: interface language, account locale (signed in, keepalive)
 * and a full navigation to the same page under the other prefix.
 */
export function CompactLanguageToggle() {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const setLocale = useSetLocale(http)
  const active: UiLocale = i18n.language === 'en' ? 'en' : 'fr'

  return (
    <div role="group" aria-label={t('language.label')} className="flex items-center">
      {SUPPORTED_LOCALES.map((locale, index) => (
        <span key={locale} className="flex items-center">
          {index > 0 ? (
            <span aria-hidden className="px-0.5 text-[12px] text-muted">
              ·
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void setUiLocale(locale)
              if (viewer) setLocale.mutate(locale)
              const next = pathInLocale(window.location.pathname, locale)
              if (next !== window.location.pathname) {
                window.location.assign(next + window.location.search)
              }
            }}
            aria-pressed={locale === active}
            lang={HTML_LANG[locale]}
            className={cn(
              'min-h-[36px] rounded-[var(--r-sm)] px-1.5 text-[12.5px] uppercase tracking-[0.02em]',
              locale === active ? 'font-bold text-ink' : 'text-muted hover:text-ink-soft',
            )}
          >
            {locale}
          </button>
        </span>
      ))}
    </div>
  )
}
